import { AFFECTED_AREA_OPTIONS } from "../../settings/settingsOptions";
import { formatCalendarDay } from "./coachFormat";

/*
 * Labels for the coach's enums (contract §4.2, §5.4) and the navigation each
 * coach link leads to (§4.1). Links never open a builder: opening one creates
 * a draft. The settings drawer has no deep link below the Training Profile
 * menu, so every profile section and Pain issues land there with a hint.
 */

export const MEMORY_KIND_OPTIONS = Object.freeze([
  { value: "TRAINING_HABIT", label: "Habit" },
  { value: "GOAL", label: "Goal" },
  { value: "CONSTRAINT", label: "Constraint" },
  { value: "EXERCISE_PREFERENCE", label: "Exercise preference" },
  { value: "TRIED_STRATEGY", label: "Tried before" },
  { value: "COMMUNICATION", label: "How to talk to me" },
]);

export function memoryKindLabel(kind) {
  return MEMORY_KIND_OPTIONS.find((option) => option.value === kind)?.label || "Note";
}

const PROFILE_SECTION_LABELS = Object.freeze({
  availability: "Availability",
  equipment: "Environment",
  manualBlockedExerciseIds: "Movement Constraints",
  musclePriorities: "Goals",
  experience: "Experience",
});

export function describeCoachLink(link) {
  switch (link?.kind) {
    case "TRAINING_PROFILE": {
      const section = PROFILE_SECTION_LABELS[link.section];
      return {
        key: `profile-${link.section || "root"}`,
        label: "Open Training Profile",
        text: "Training Profile",
        hint: section || null,
        target: { type: "trainingProfile" },
      };
    }
    case "PAIN_ISSUES":
      return {
        key: "pain",
        label: "Open Pain issues",
        text: "Pain issues",
        hint: "Movement Constraints",
        target: { type: "trainingProfile" },
      };
    case "PROGRAM":
      return { key: "program", label: "Open Program", text: "Program", hint: null, target: { type: "route", to: "/program" } };
    default:
      return null;
  }
}

export const RECALL_SOURCE_LABELS = Object.freeze({
  MESSAGE_USER: "You",
  MESSAGE_COACH: "Coach",
  MEMORY: "Memory",
});

export const CHECKIN_SCALES = Object.freeze({
  energy: { label: "Energy", options: [["LOW", "Low"], ["NORMAL", "Normal"], ["HIGH", "High"]] },
  sleep: { label: "Sleep", options: [["POOR", "Poor"], ["OK", "OK"], ["GOOD", "Good"]] },
  stress: { label: "Stress", options: [["HIGH", "High"], ["NORMAL", "Normal"], ["LOW", "Low"]] },
});

export const CONSTRAINT_TYPE_OPTIONS = Object.freeze([
  { value: "TRAVEL", label: "Travel" },
  { value: "EXAM", label: "Exam" },
  { value: "EVENT", label: "Event" },
  { value: "OTHER", label: "Other" },
]);

export const PAIN_AREA_OPTIONS = AFFECTED_AREA_OPTIONS;

function optionLabel(options, value) {
  return options.find((option) => option.value === value)?.label || null;
}

function scaleLabel(field, value) {
  return CHECKIN_SCALES[field].options.find(([key]) => key === value)?.[1] || null;
}

// One short line per answered field, in the blueprint's order (§12.6).
export function summarizeCheckin(checkin) {
  if (!checkin) return [];
  const parts = [];
  ["energy", "sleep", "stress"].forEach((field) => {
    const label = scaleLabel(field, checkin[field]);
    if (label) parts.push(`${CHECKIN_SCALES[field].label} ${label.toLowerCase()}`);
  });
  if (checkin.pain?.present === true) {
    const area = optionLabel(PAIN_AREA_OPTIONS, checkin.pain.area);
    parts.push(area ? `Pain: ${area.toLowerCase()}` : "Pain reported");
  } else if (checkin.pain?.present === false) {
    parts.push("No pain");
  }
  if (checkin.upcomingConstraint?.present === true) {
    const type = optionLabel(CONSTRAINT_TYPE_OPTIONS, checkin.upcomingConstraint.type) || "Constraint";
    const day = formatCalendarDay(checkin.upcomingConstraint.date);
    parts.push(day ? `${type} ${day}` : type);
  }
  if (checkin.bodyWeight?.value != null) {
    parts.push(`${checkin.bodyWeight.value} ${checkin.bodyWeight.unit === "LB" ? "lb" : "kg"}`);
  }
  if (checkin.note) parts.push("Note added");
  return parts;
}

/*
 * Recall sources worth showing under a reply. The backend returns up to 5
 * excerpts that often say the same thing three times (the memory, the message
 * it came from, the coach's own acknowledgment). Keep one per idea, preferring
 * the memory, then the athlete's words, then the coach's; drop any excerpt the
 * reply itself already says.
 */
const SOURCE_PRIORITY = Object.freeze({ MEMORY: 0, MESSAGE_USER: 1, MESSAGE_COACH: 2 });

function stems(text) {
  return new Set(
    String(text || "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((word) => word.length > 2)
      .map((word) => word.slice(0, 5))
  );
}

function sameIdea(a, b) {
  if (!a.size || !b.size) return false;
  let shared = 0;
  a.forEach((stem) => {
    if (b.has(stem)) shared += 1;
  });
  return shared / Math.min(a.size, b.size) >= 0.75;
}

export function recallSources(recall, replyText = "") {
  const reply = stems(replyText);
  const kept = [];
  [...(recall?.excerpts || [])]
    .map((excerpt, index) => ({ ...excerpt, index, stems: stems(excerpt.text) }))
    .sort((a, b) => (SOURCE_PRIORITY[a.source] ?? 3) - (SOURCE_PRIORITY[b.source] ?? 3) || a.index - b.index)
    .forEach((excerpt) => {
      if (!excerpt.stems.size || sameIdea(excerpt.stems, reply)) return;
      if (kept.some((other) => sameIdea(other.stems, excerpt.stems))) return;
      kept.push(excerpt);
    });
  return kept.map(({ stems: _stems, index: _index, ...excerpt }) => excerpt);
}
